import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Notification from '../../../components/Notification/index.js';
import styles from '../styles.module.css';

const SCOPE_DETAILS = {
  'orders_shipments.read': {
    title: 'Read orders & shipments',
    description: 'View orders, shipments, and tracking info',
  },
  'orders_shipments.write': {
    title: 'Write orders & shipments',
    description: 'Create, update, and cancel orders & shipments',
  },
  'settings.read': {
    title: 'Read settings',
    description: 'View account configuration and preferences',
  },
  'settings.write': {
    title: 'Write settings',
    description: 'Modify account configuration and preferences',
  },
};

function PermissionsStep({
  authorization,
  error,
  isSubmitting,
  onApprove,
  onDeny,
}) {
  const requiredScopes = authorization.required_scopes || [];
  const optionalScopes = authorization.optional_scopes || [];
  const [selectedOptionalScopes, setSelectedOptionalScopes] = useState([]);

  const toggleScope = (scope) => {
    setSelectedOptionalScopes((selected) =>
      selected.includes(scope)
        ? selected.filter((selectedScope) => selectedScope !== scope)
        : [...selected, scope],
    );
  };

  const renderScope = (scope, required) => {
    const details = SCOPE_DETAILS[scope] || {
      title: scope,
      description: '',
    };
    const checked = required || selectedOptionalScopes.includes(scope);

    return (
      <label
        className={`${styles.permission} ${
          checked ? styles.selectedPermission : ''
        }`}
        key={scope}
      >
        <input
          className={styles.checkbox}
          type="checkbox"
          checked={checked}
          disabled={required || isSubmitting}
          aria-label={details.title}
          onChange={() => toggleScope(scope)}
        />
        <span className={styles.permissionDetails}>
          <strong>{details.title}</strong>
          {details.description && <span>{details.description}</span>}
        </span>
        {required && <span className={styles.required}>Required</span>}
      </label>
    );
  };

  return (
    <section className={styles.card} data-testid="oauth-permissions">
      <div className={styles.securityIcon} aria-hidden="true">
        ✓
      </div>
      <h1>Select Permissions</h1>
      <p className={styles.hint}>
        Choose what <strong>{authorization.name}</strong> is allowed to do
      </p>

      <div className={styles.permissions}>
        {requiredScopes.map((scope) => renderScope(scope, true))}
        {optionalScopes.map((scope) => renderScope(scope, false))}
      </div>

      {error && <Notification type="error" message={error} />}

      <div className={styles.actions}>
        <button
          className={styles.denyButton}
          type="button"
          disabled={isSubmitting}
          onClick={onDeny}
        >
          {isSubmitting ? 'Please wait…' : 'Deny'}
        </button>
        <button
          className={styles.primaryButton}
          type="button"
          disabled={isSubmitting}
          onClick={() =>
            onApprove([...requiredScopes, ...selectedOptionalScopes])
          }
        >
          {isSubmitting ? 'Please wait…' : 'Approve'}
        </button>
      </div>
    </section>
  );
}

PermissionsStep.propTypes = {
  authorization: PropTypes.shape({
    name: PropTypes.string.isRequired,
    required_scopes: PropTypes.arrayOf(PropTypes.string),
    optional_scopes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  error: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDeny: PropTypes.func.isRequired,
};

export default PermissionsStep;
